/**
 * SectionCard component for marketing pages
 * Provides consistent section card styling with icon, title, and content
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconWrapper } from '@/components/marketing';
import { FEATURE_COLORS, type FeatureColor } from '@/lib/design-tokens';

export interface SectionCardProps {
  /**
   * Icon to display in the section header
   */
  icon: LucideIcon;
  /**
   * Title of the section
   */
  title: string;
  /**
   * Content to display in the section
   */
  children: React.ReactNode;
  /**
   * Color theme for the icon and hover effects
   */
  color?: FeatureColor;
  /**
   * Optional custom className for the section wrapper
   */
  className?: string;
  /**
   * Optional ID for the section heading (for aria-labelledby)
   */
  id?: string;
}

/**
 * Reusable section card component for marketing pages
 *
 * Provides consistent styling for content sections with icons, titles, and hover effects.
 * Used across security, about, and contact pages for visual consistency.
 *
 * @param icon - Lucide icon component to display
 * @param title - Section heading text
 * @param children - Section content (paragraphs, lists, etc.)
 * @param color - Color theme (default: 'blue')
 * @param className - Optional custom className
 * @param id - Optional section ID
 *
 * @example
 * ```tsx
 * <SectionCard
 *   icon={ShieldCheck}
 *   title="Built for trust"
 *   color="blue"
 * >
 *   <p>Protecting your email data is core to Email Ferret...</p>
 * </SectionCard>
 * ```
 *
 * @see {@link IconWrapper} - Icon container component
 * @see {@link FEATURE_COLORS} - Available color themes
 */
export function SectionCard({
  icon,
  title,
  children,
  color = 'blue',
  className = '',
  id,
}: SectionCardProps): React.ReactElement {
  const colorConfig = FEATURE_COLORS[color];

  return (
    <section
      id={id}
      className={cn(
        'bg-card/80 backdrop-blur-sm rounded-2xl border shadow-xl p-8 md:p-10 hover:shadow-2xl transition-all duration-300',
        colorConfig.hoverBorder,
        className
      )}
    >
      <div className="flex items-start gap-4 mb-6">
        <IconWrapper icon={icon} color={color} size="xl" className="flex-shrink-0 mb-0" />
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 break-words">{title}</h2>
          <div className="text-lg leading-relaxed break-words">{children}</div>
        </div>
      </div>
    </section>
  );
}
